import { Button } from '@finn/design-system/atoms/button';
import { Portal } from '@finn/design-system/atoms/portal';
import { Check } from '@finn/design-system/icons/check';
import { GroupFilled } from '@finn/design-system/icons/group-filled';
import { cn, useIntl } from '@finn/ui-utils';
import Link from 'next/link';
import * as React from 'react';
import { useMemo } from 'react';

import { trackCTAClick } from '~/modules/landingPages/components/StickyCTAPanel/StickyCTAPanel.tracking';

import StickyPanelContainer from '../StickyPanelContainer';

const DEFAULT_CTA_HREF = '/subscribe';

const DEFAULT_ENTRY_OFFSET = 250;

type DescriptionItem = {
  icon: { key: '' | 'check' };
  label: string;
};

type Theme = {
  key: 'light' | 'dark';
};

export type StickyCTAPanelConfig = {
  enabled?: boolean;
  theme?: Theme;
  title?: string;
  description_items?: DescriptionItem[];
  customer_count_label?: string;
  cta_label?: string;
  cta_href?: string;
  cta_tracking_type?: string;
  entry_offset?: number;
};

type Props = {
  data?: StickyCTAPanelConfig;
};

const StickyCTAPanel: React.FC<Props> = ({ data }) => {
  const i18n = useIntl();

  const panelConfig = useMemo<StickyCTAPanelConfig>(
    () => ({
      enabled: data?.enabled ?? true,

      theme: data?.theme ?? { key: 'dark' },

      title: data?.title || i18n.formatMessage('general.stickyCTAPanel.title'),

      description_items:
        data?.description_items?.length > 0
          ? data?.description_items
          : [
              {
                icon: { key: 'check' },
                label: i18n.formatMessage(
                  'general.stickyCTAPanel.description_item_1'
                ),
              },

              {
                icon: { key: 'check' },
                label: i18n.formatMessage(
                  'general.stickyCTAPanel.description_item_2'
                ),
              },

              {
                icon: { key: 'check' },
                label: i18n.formatMessage(
                  'general.stickyCTAPanel.description_item_3'
                ),
              },
            ],

      customer_count_label: data?.customer_count_label,

      cta_label:
        data?.cta_label ||
        i18n.formatMessage('general.stickyCTAPanel.cta_label'),

      cta_href: data?.cta_href ?? DEFAULT_CTA_HREF,

      cta_tracking_type: data?.cta_tracking_type,

      entry_offset: data?.entry_offset ?? DEFAULT_ENTRY_OFFSET,
    }),
    [data, i18n]
  );

  if (!panelConfig.enabled) {
    return null;
  }

  return (
    <Portal>
      <StickyPanelContainer entryOffset={panelConfig.entry_offset}>
        <div
          className={cn('bg-white fill-black py-4', {
            'bg-black fill-white': panelConfig.theme.key === 'dark',
          })}
        >
          <div className="container">
            <div className="flex items-center justify-between">
              <div className="flex flex-col gap-3">
                <span
                  className={`global-t6-semibold md:global-t5-semibold break-words ${panelConfig.theme.key === 'dark' ? 'text-white' : 'text-black'}`}
                >
                  {panelConfig.title}
                </span>

                {panelConfig.description_items.length > 0 && (
                  <div className="hidden items-center md:flex">
                    {panelConfig.description_items.map((item) => (
                      <>
                        <div
                          key={item.label}
                          className={`body-16-regular mr-4 flex items-center ${panelConfig.theme.key === 'dark' ? 'text-white' : 'text-black'} '& + &':ml-4`}
                        >
                          <span className="flex items-center">
                            {item.icon.key === 'check' && (
                              <Check className="mr-1" />
                            )}

                            <span>{item.label}</span>
                          </span>
                        </div>
                      </>
                    ))}
                  </div>
                )}
              </div>

              <div className="flex items-center gap-8">
                {panelConfig.customer_count_label && (
                  <div
                    className={`body-16-semibold flex items-center gap-2 ${panelConfig.theme.key === 'dark' ? 'text-white' : 'text-black'}`}
                  >
                    <GroupFilled fill="currentColor" />
                    {panelConfig.customer_count_label}
                  </div>
                )}

                <Link href={panelConfig.cta_href} passHref legacyBehavior>
                  <Button
                    variant="action"
                    className={panelConfig.theme.key === 'dark' && 'text-white'}
                    onClick={() => trackCTAClick(panelConfig.cta_tracking_type)}
                  >
                    {panelConfig.cta_label}
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </StickyPanelContainer>
    </Portal>
  );
};

export default React.memo(StickyCTAPanel);
